import {
  createTestTargetsBulk,
  updateTestTargetsBulk,
  deleteTestTargetsBulk,
  type BulkCreateInput,
  type BulkUpdateInput,
} from "./testTargetService";
import type { SSTFApiClient } from "./api";
import type { AppTestTarget } from "../utils/spec-import/types";
import type { RegisterTestTargetsResponse, FailedTestTargetItem } from "./operationLogService";

export type RegistrationProgressCallback = (
  current: number,
  total: number,
  message: string,
) => void;

export interface RegisterSpecImportArgs {
  resolvedTargets: AppTestTarget[];
  /**
   * 既存ターゲットにマッチした test_target_id の集合。
   *
   * `matchExistingTargets` は `resolvedTargets[].id` / `test_target_id` を既存ターゲットの UUID で
   * **直接上書き** するため、本 Set は「これらの id は既存マッチ済み = update 対象」のフラグとして機能する。
   * UUID マップを別途持つ必要はない (上書き済みなので resolvedTargets から直接読める)。
   */
  matchedExistingIds: Set<string>;
  /** 削除対象の既存 test_target_id。create/update の前に削除フェーズで処理される。 */
  deleteCandidateIds?: Set<string>;
  /**
   * 削除選択された親の子孫 ID マップ。key = 削除対象 ID、value = その子孫 ID 配列。
   * `expandWithDescendants` で葉→親順の削除 ID 列を構築するために使う。
   */
  existingChildrenOf?: Record<string, string[]>;
  /**
   * 既存ターゲットの id → name 逆引きマップ。削除失敗時に UUID の代わりに人間可読な名前を
   * failed_items.target_name に入れるために使う。子孫 ID も名前解決できるよう全既存ターゲットを網羅すること。
   */
  existingNamesById?: Record<string, string>;
  apiClient: SSTFApiClient | null | undefined;
  workspaceId: string;
  projectId: string;
  onProgress?: RegistrationProgressCallback;
}

/**
 * 仕様書インポートで生成された AppTestTarget[] を SSTF に直接登録する。
 *
 * - depth 順（service → component → view → section/overlay → item）にソートして親 / 参照先が先に登録される
 * - 仮 UUID → SSTF 実 UUID のマップを保持し、後続ターゲットの parentTestTargetId / params の参照を実 ID に書き換える
 * - 既存マッチ (matchedExistingIds.has(test_target_id)) は updateTestTarget、それ以外は createTestTarget
 * - エラーは per-target で集約して継続
 */
export async function registerSpecImportTargets(
  args: RegisterSpecImportArgs,
): Promise<RegisterTestTargetsResponse & { createdServiceIds: string[] }> {
  const {
    resolvedTargets,
    matchedExistingIds,
    deleteCandidateIds,
    existingChildrenOf,
    existingNamesById,
    apiClient,
    workspaceId,
    projectId,
    onProgress,
  } = args;

  if (!apiClient || !workspaceId || !projectId) {
    throw new Error("API設定が不完全です。");
  }

  const failedItems: FailedTestTargetItem[] = [];
  let registeredCount = 0;
  let deletedCount = 0;

  // --- 削除フェーズ (create/update より前に実行) ---
  // 葉→親順（子孫が前）で ID 列を構築し、1 回の一括 DELETE で送る。
  // SSTF の親子削除順序制約は ID 列の並び順で担保する。
  if (deleteCandidateIds && deleteCandidateIds.size > 0) {
    const idsToDelete = expandWithDescendants(deleteCandidateIds, existingChildrenOf ?? {});
    try {
      await deleteTestTargetsBulk(idsToDelete, apiClient, workspaceId, projectId);
      deletedCount += idsToDelete.length;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      for (const id of idsToDelete) {
        failedItems.push({
          target_name: existingNamesById?.[id] ?? id,
          reason: message,
          item_type: "target",
        });
      }
    }
  }

  // depth: service=0, component=1, view=2, section/overlay=3, component-ref=4, item=4
  // Component を View / section / overlay より先に登録するのは、component-ref の
  // `params.referenceTargetId` が `resolveParamsIds` で解決される際に参照先 component が
  // `idMap` に登録済みである必要があるため。同様に component-ref の配置先となる
  // view / section / overlay も component-ref より前に登録済みである必要がある。
  // (lib 出力順は仕様書記述順で確定しないため depthOf 側で順序を保証する)
  const depthOf = (t: AppTestTarget): number => {
    if (t.type === "service") return 0;
    if (t.type === "component") return 1;
    if (t.type === "view") return 2;
    if (t.type === "section" || t.type === "overlay") return 3;
    if (t.type === "component-ref") return 4; // 配置先 view/section/overlay と参照先 component が先に登録済みになる
    return 4; // item
  };

  // 仮 UUID → 実 UUID
  const idMap = new Map<string, string>();
  // params 内で「batch 内で未解決」の参照を削除した累計件数。
  // failedItems には登録失敗以外を入れないため、登録成功と並走する警告は別経路で集計。
  let totalDroppedRefs = 0;

  // batch 内の全 target id 集合 (resolveParamsIds で「batch 内 vs batch 外」を判定するため)
  const batchKeys = new Set<string>();
  for (const t of resolvedTargets) {
    if (t.test_target_id) batchKeys.add(t.test_target_id);
    if (t.id && t.id !== t.test_target_id) batchKeys.add(t.id);
  }

  // --- CREATE フェーズ ---
  // 新規ターゲットを「depth グループ → 同 depth 内トポロジカルレベル」の 2 段階で分割し、
  // 各レベルを一括 CREATE する。
  // depth でグループを分けることで Component (depth=1) が View (depth=2) より先に登録され、
  // View の params.component_ids 参照が idMap で解決できるようになる。
  // 同 depth 内の parent-child 参照 (例: item の親が同じ画面の別 item) は buildCreateLevels で解決。
  const createTargets = resolvedTargets.filter(
    (t) => !matchedExistingIds.has(t.test_target_id ?? t.id),
  );
  // depth 別グループ化（Map で挿入順を保持）
  const depthGroups = new Map<number, AppTestTarget[]>();
  for (const t of createTargets) {
    const d = depthOf(t);
    if (!depthGroups.has(d)) depthGroups.set(d, []);
    depthGroups.get(d)!.push(t);
  }
  const sortedDepths = Array.from(depthGroups.keys()).sort((a, b) => a - b);
  // 各 depth グループ内で topological sort + buildCreateLevels を適用し、全レベルをフラット化
  const createLevels: AppTestTarget[][] = [];
  for (const depth of sortedDepths) {
    const group = depthGroups.get(depth)!;
    const sortedGroup = topologicalSortByParent(group);
    // buildCreateLevels 実行時点での resolved 集合: matchedExistingIds + これまでのレベルで idMap に登録済みの ID
    // ただし buildCreateLevels は matchedExistingIds だけで初期化するため、
    // 前 depth グループで作成されたターゲットの仮 ID を resolved に追加して渡す
    const resolvedSoFar = new Set<string>(matchedExistingIds);
    idMap.forEach((_, key) => resolvedSoFar.add(key));
    // 前の depth グループに属するターゲットの仮 ID も resolved 扱いにする
    for (const lv of createLevels) {
      for (const t of lv) {
        const key = t.test_target_id ?? t.id;
        if (key) resolvedSoFar.add(key);
        if (t.id && t.id !== key) resolvedSoFar.add(t.id);
      }
    }
    const groupLevels = buildCreateLevels(sortedGroup, resolvedSoFar);
    createLevels.push(...groupLevels);
  }
  const totalLevels = createLevels.length;

  for (let levelIdx = 0; levelIdx < createLevels.length; levelIdx++) {
    const level = createLevels[levelIdx];
    const bulkInputs: BulkCreateInput[] = [];
    const skipped: AppTestTarget[] = [];

    for (const target of level) {
      // 親 ID 解決:
      // - 新規ターゲット (本 batch で作成) → idMap で仮 UUID → 実 UUID へ置換
      // - 既存ターゲット (matchExistingTargets で test_target_id 上書き済み) → そのまま使う
      // どちらにも該当しない場合は依存登録失敗とみなし failedItem として記録 + skip
      const originalParentId = target.parentId ?? target.parent_test_target_id;
      let resolvedParentId: string | undefined;
      if (originalParentId) {
        if (idMap.has(originalParentId)) {
          resolvedParentId = idMap.get(originalParentId);
        } else if (matchedExistingIds.has(originalParentId)) {
          resolvedParentId = originalParentId;
        } else {
          failedItems.push({
            target_name: target.name,
            reason: `親 ID "${originalParentId}" が解決できません (親ターゲットが本 batch で登録失敗した可能性)`,
            item_type: "target",
          });
          skipped.push(target);
          continue;
        }
      }

      // params 内の参照 (transitions / component_ids / componentConditions) を解決。
      // batch 内なのに idMap に無い (未登録 = 新規 create 失敗) ID は drop。
      // matchedExistingIds に含まれる ID は backend 上で実在するためそのまま forward。
      const { params: resolvedParams, droppedRefs } = resolveParamsIds(
        target.params,
        idMap,
        batchKeys,
        matchedExistingIds,
      );
      if (droppedRefs > 0) {
        // 登録自体は成功する見込みなので failedItems には入れず、totalDroppedRefs で集約する。
        // 詳細はコンソールにも出して開発デバッグを容易に。
        totalDroppedRefs += droppedRefs;
        // eslint-disable-next-line no-console
        console.warn(
          `[spec-import] ${target.name}: ${droppedRefs} 件の未解決参照を params から削除`,
        );
      }

      bulkInputs.push({
        tempKey: target.test_target_id ?? target.id,
        data: {
          name: target.name,
          type: target.type,
          description: target.description ?? "",
          displayOrder: target.displayOrder ?? 1.0,
          parentId: resolvedParentId,
          params: resolvedParams,
        },
      });
    }

    if (bulkInputs.length === 0) continue;

    onProgress?.(levelIdx + 1, totalLevels, "登録中…");

    try {
      const { idMap: levelIdMap } = await createTestTargetsBulk(
        bulkInputs,
        apiClient,
        workspaceId,
        projectId,
      );
      levelIdMap.forEach((realId, tempKey) => idMap.set(tempKey, realId));
      registeredCount += bulkInputs.length;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      for (const input of bulkInputs) {
        failedItems.push({
          target_name: input.data.name,
          reason: message,
          item_type: "target",
        });
      }
    }
  }

  // CREATE 直後の params 再同期:
  // 初回 import では、batch 内の仮 ID (view:..., component:...) が実 UUID に確定するのは
  // createTestTargetsBulk の応答後になる。さらに SSTF 側は create と update で params 正規化の
  // 挙動が異なる場合があるため、新規作成分は全 create 完了後に「2 回目 import 相当」の
  // 実 ID 解決済み params で一括 UPDATE する。
  const postCreateUpdateInputs: BulkUpdateInput[] = [];
  for (const target of createTargets) {
    const targetKey = target.test_target_id ?? target.id;
    const realTargetId = idMap.get(targetKey);
    if (!realTargetId) continue; // create 失敗済み。failedItems は create フェーズで記録済み。

    const originalParentId = target.parentId ?? target.parent_test_target_id;
    let resolvedParentId: string | undefined;
    if (originalParentId) {
      if (idMap.has(originalParentId)) {
        resolvedParentId = idMap.get(originalParentId);
      } else if (matchedExistingIds.has(originalParentId)) {
        resolvedParentId = originalParentId;
      } else {
        failedItems.push({
          target_name: target.name,
          reason: `作成後の再同期で親 ID "${originalParentId}" が解決できません`,
          item_type: "target",
        });
        continue;
      }
    }

    const { params: resolvedParams, droppedRefs } = resolveParamsIds(
      target.params,
      idMap,
      batchKeys,
      matchedExistingIds,
    );
    if (droppedRefs > 0) {
      totalDroppedRefs += droppedRefs;
      // eslint-disable-next-line no-console
      console.warn(
        `[spec-import] ${target.name}: 作成後再同期で ${droppedRefs} 件の未解決参照を params から削除`,
      );
    }

    postCreateUpdateInputs.push({
      id: realTargetId,
      data: {
        name: target.name,
        type: target.type,
        description: target.description ?? "",
        displayOrder: target.displayOrder ?? 1.0,
        parentId: resolvedParentId,
        params: resolvedParams,
      },
    });
  }

  if (postCreateUpdateInputs.length > 0) {
    try {
      await updateTestTargetsBulk(postCreateUpdateInputs, apiClient, workspaceId, projectId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      for (const input of postCreateUpdateInputs) {
        failedItems.push({
          target_name: input.data.name,
          reason: `作成後の params 再同期に失敗しました: ${message}`,
          item_type: "target",
        });
      }
    }
  }

  // --- UPDATE フェーズ ---
  // 既存マッチターゲットをまとめて一括 UPDATE する。
  const updateTargets = resolvedTargets.filter(
    (t) => matchedExistingIds.has(t.test_target_id ?? t.id),
  );

  if (updateTargets.length > 0) {
    const bulkUpdateInputs: BulkUpdateInput[] = [];

    for (const target of updateTargets) {
      const originalParentId = target.parentId ?? target.parent_test_target_id;
      let resolvedParentId: string | undefined;
      if (originalParentId) {
        if (idMap.has(originalParentId)) {
          resolvedParentId = idMap.get(originalParentId);
        } else if (matchedExistingIds.has(originalParentId)) {
          resolvedParentId = originalParentId;
        } else {
          failedItems.push({
            target_name: target.name,
            reason: `親 ID "${originalParentId}" が解決できません (親ターゲットが本 batch で登録失敗した可能性)`,
            item_type: "target",
          });
          continue;
        }
      }

      const { params: resolvedParams, droppedRefs } = resolveParamsIds(
        target.params,
        idMap,
        batchKeys,
        matchedExistingIds,
      );
      if (droppedRefs > 0) {
        totalDroppedRefs += droppedRefs;
        // eslint-disable-next-line no-console
        console.warn(
          `[spec-import] ${target.name}: ${droppedRefs} 件の未解決参照を params から削除`,
        );
      }

      bulkUpdateInputs.push({
        id: target.test_target_id ?? target.id,
        data: {
          name: target.name,
          type: target.type,
          description: target.description ?? "",
          displayOrder: target.displayOrder ?? 1.0,
          parentId: resolvedParentId,
          params: resolvedParams,
        },
      });
    }

    if (bulkUpdateInputs.length > 0) {
      try {
        await updateTestTargetsBulk(bulkUpdateInputs, apiClient, workspaceId, projectId);
        registeredCount += bulkUpdateInputs.length;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        for (const input of bulkUpdateInputs) {
          failedItems.push({
            target_name: input.data.name,
            reason: message,
            item_type: "target",
          });
        }
      }
    }
  }

  const droppedSuffix = totalDroppedRefs > 0 ? ` (params 内の未解決参照 ${totalDroppedRefs} 件を削除)` : "";

  // 完了メッセージ: 登録と削除を分けて表現する。削除 0 件なら登録のみの文言にする。
  let completionMessage: string;
  if (failedItems.length === 0) {
    if (deletedCount > 0) {
      completionMessage = `${registeredCount} 件登録、${deletedCount} 件削除しました${droppedSuffix}`;
    } else {
      completionMessage = `${registeredCount} 件のテストターゲットを登録しました${droppedSuffix}`;
    }
  } else {
    const parts: string[] = [];
    // 削除のみの操作で「0 件登録」を出さないため、登録ありか削除なしのときだけ登録件数を表示
    if (registeredCount > 0 || deletedCount === 0) parts.push(`${registeredCount} 件登録`);
    if (deletedCount > 0) parts.push(`${deletedCount} 件削除`);
    parts.push(`${failedItems.length} 件失敗`);
    completionMessage = parts.join("、") + droppedSuffix;
  }

  // 新規作成した service の実 ID（サイドバーで自動表示 ON にするため呼び出し元へ返す）
  const createdServiceIds = createTargets
    .filter((t) => t.type === "service")
    .map((t) => idMap.get(t.test_target_id ?? t.id))
    .filter((id): id is string => id != null);

  return {
    registered_count: registeredCount,
    deleted_count: deletedCount > 0 ? deletedCount : undefined,
    message: completionMessage,
    failed_items: failedItems.length > 0 ? failedItems : undefined,
    dropped_refs: totalDroppedRefs > 0 ? totalDroppedRefs : undefined,
    createdServiceIds,
  };
}

/**
 * params 内の test_target_id 参照 (transitions / component_ids / componentConditions) を
 * idMap で実 ID に解決する。
 *
 * 解決ルール:
 * - `idMap.has(id)` → batch 内で登録済み、実 UUID に置換
 * - `matchedExistingIds.has(id)` → 既存マッチ済み (backend 上で実在)、そのまま forward
 * - `batchKeys.has(id) && !idMap.has(id)` → batch 内新規だがまだ未登録 (create 失敗等) → 削除
 * - 上記いずれでもない → batch 外の既存 UUID とみなしてそのまま forward
 *
 * parent 解決と同じ判定ロジックを採用し、existing target の update 失敗時に
 * その UUID への params 参照が silent drop される非対称バグを回避する。
 */
function resolveParamsIds(
  params: unknown,
  idMap: Map<string, string>,
  batchKeys: ReadonlySet<string>,
  matchedExistingIds: ReadonlySet<string>,
): { params: Record<string, unknown> | undefined; droppedRefs: number } {
  if (!params || typeof params !== "object") return { params: undefined, droppedRefs: 0 };
  const cloned: Record<string, unknown> = { ...(params as Record<string, unknown>) };
  let droppedRefs = 0;
  const resolveId = (id: string): string | undefined => {
    if (idMap.has(id)) return idMap.get(id);
    if (matchedExistingIds.has(id)) return id; // 既存マッチ済み (update 失敗でも backend に存在)
    if (batchKeys.has(id)) return undefined; // batch 内新規で未登録 → drop
    return id; // batch 外 → そのまま使う
  };

  // transitions[].targetId — 解決失敗は entry ごと削除、targetId 空の degenerate データは pass-through
  if (Array.isArray(cloned.transitions)) {
    cloned.transitions = (cloned.transitions as { targetId?: string; [k: string]: unknown }[])
      .flatMap((tr) => {
        if (!tr.targetId) return [tr]; // 草稿状態 (targetId 未指定) はそのまま保持
        const resolvedId = resolveId(tr.targetId as string);
        if (!resolvedId) {
          droppedRefs += 1;
          return [];
        }
        return [{ ...tr, targetId: resolvedId }];
      });
  }

  // component_ids[] — 解決失敗は ID ごと削除、空文字は pass-through
  if (Array.isArray(cloned.component_ids)) {
    cloned.component_ids = (cloned.component_ids as string[]).flatMap((cid) => {
      if (!cid) return [cid];
      const resolvedId = resolveId(cid);
      if (!resolvedId) {
        droppedRefs += 1;
        return [];
      }
      return [resolvedId];
    });
  }

  // componentConditions[].componentId — 解決失敗は entry ごと削除、componentId 空は pass-through
  if (Array.isArray(cloned.componentConditions)) {
    cloned.componentConditions = (
      cloned.componentConditions as { componentId: string; conditions: string[] }[]
    ).flatMap((c) => {
      if (!c.componentId) return [c];
      const resolvedId = resolveId(c.componentId);
      if (!resolvedId) {
        droppedRefs += 1;
        return [];
      }
      return [{ componentId: resolvedId, conditions: c.conditions }];
    });
  }

  // referenceTargetId (component-ref) — 解決失敗は drop せず未解決を残さないよう entry 自体は保持
  if (typeof cloned.referenceTargetId === "string" && cloned.referenceTargetId) {
    const resolved = resolveId(cloned.referenceTargetId as string);
    if (resolved) cloned.referenceTargetId = resolved;
    // 解決不能（batch外既存UUID 等）はそのまま pass-through（transitions.targetId と同方針）
  }

  return { params: cloned, droppedRefs };
}

/**
 * 配列の相対順を可能な限り保ちつつ、parent が child より後にある場合のみ前置する。
 *
 * 入力配列は事前に depthOf でソート済みであることを前提とする。
 * - 同 depth 内 (例: item-item の親子) で parent が後にあるケースのみ swap
 * - depth 違いの parent (Item の親 View 等) は既に前にあるため touch しない (相対順を保つ)
 *
 * `test_target_id` と `id` が異なるターゲット (backend 由来の既存マッチ) に対応するため、
 * `byKey` には両 ID を登録して lookup できるようにする。
 */
function topologicalSortByParent(targets: AppTestTarget[]): AppTestTarget[] {
  const byKey = new Map<string, AppTestTarget>();
  const positions = new Map<string, number>();
  targets.forEach((t, i) => {
    if (t.test_target_id) {
      byKey.set(t.test_target_id, t);
      positions.set(t.test_target_id, i);
    }
    if (t.id && t.id !== t.test_target_id) {
      byKey.set(t.id, t);
      positions.set(t.id, i);
    }
  });

  const placed = new Set<string>();
  const result: AppTestTarget[] = [];

  const visit = (t: AppTestTarget): void => {
    const selfKey = t.test_target_id ?? t.id;
    if (!selfKey || placed.has(selfKey)) return;
    placed.add(selfKey);
    if (t.id && t.id !== selfKey) placed.add(t.id);
    const parentKey = t.parentId ?? t.parent_test_target_id;
    if (parentKey) {
      const parent = byKey.get(parentKey);
      // parent が batch 内かつ自身より後ろにある場合のみ先に visit (前置)。
      // 既に前にある (= positions が小さい) 場合は visit 不要 (= depth ソートで配置済み)。
      const selfPos = positions.get(selfKey);
      const parentPos = positions.get(parentKey);
      if (parent && selfPos !== undefined && parentPos !== undefined && parentPos > selfPos) {
        visit(parent);
      }
    }
    result.push(t);
  };

  for (const t of targets) visit(t);
  return result;
}

/**
 * CREATE 対象のターゲット（新規ターゲット）を「親が確定済み（既存 or 親なし or 前レベル）」のレベル群に分割する。
 *
 * 親 ID は `parentId ?? parent_test_target_id` で判定。
 * 確定済み集合 = matchedExistingIds（既存マッチ）∪ これまで配置したレベルの ID。
 * 親が確定済み or 親がないものを次レベルへ進める。
 * 1 周で 1 件も配置できないケース（循環依存等）は残り全部を最終レベルに入れて打ち切る（無限ループ防止）。
 * 戻り値はトポロジカルレベル順の 2D 配列で、各レベルは親が確定済みのターゲット群。
 */
export function buildCreateLevels(
  createTargets: AppTestTarget[],
  matchedExistingIds: ReadonlySet<string>,
): AppTestTarget[][] {
  const keyOf = (t: AppTestTarget) => t.test_target_id ?? t.id;
  const parentOf = (t: AppTestTarget) => t.parentId ?? t.parent_test_target_id;
  const resolved = new Set<string>(matchedExistingIds);
  const remaining = [...createTargets];
  const levels: AppTestTarget[][] = [];

  while (remaining.length > 0) {
    const ready = remaining.filter((t) => {
      const p = parentOf(t);
      return !p || resolved.has(p);
    });
    if (ready.length === 0) {
      // 1 周で 1 件も配置できない場合、無限ループを避けるため残り全部を最終レベルに入れて打ち切る
      levels.push(remaining.splice(0));
      break;
    }
    for (const t of ready) {
      resolved.add(keyOf(t));
      if (t.id && t.id !== keyOf(t)) resolved.add(t.id);
    }
    const readyKeys = new Set(ready.map(keyOf));
    levels.push(ready);
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (readyKeys.has(keyOf(remaining[i]))) remaining.splice(i, 1);
    }
  }
  return levels;
}

/**
 * 削除候補 ID を子孫込みで展開し、葉→親順（子孫が前）の重複なし配列を返す。
 *
 * 各 id を BFS で子孫展開し、子孫を先・自身を後に配置することで SSTF の親子削除の
 * 順序制約（子を先に削除）に対応する。
 */
export function expandWithDescendants(
  ids: ReadonlySet<string>,
  childrenOf: Readonly<Record<string, string[]>>,
): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  const expand = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    // 子孫を先に展開（葉→親の順を保証）
    for (const childId of childrenOf[id] ?? []) {
      expand(childId);
    }
    result.push(id);
  };

  Array.from(ids).forEach((id) => expand(id));

  return result;
}
